import { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveFunnel } from "@nivo/funnel";
import { StatsResponse, AUCount } from "@api/src/types/responses";

export default function Stats() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{
    dau: AUCount[];
    wau: AUCount[];
    mau: AUCount[];
    cumulativeUsers: AUCount[];
    totalUsers: number;
    onboardingFunnel: {
      totalUsersForOnboarding: number;
      usersWithEmail: number;
      usersWithFirstName: number;
      usersWithNativePush: number;
    };
    roomActivity: Record<string, Array<{ roomName: string; postCount: number }>>;
    monthlyEngagement: {
      month: string;
      "Créateurs de contenu": number;
      "Engagés (post, like)": number;
      Visiteurs: number;
      Inactifs: number;
      total: number;
      "Créateurs de contenu (%)": number;
      "Engagés (post, like) (%)": number;
      "Visiteurs (%)": number;
      "Inactifs (%)": number;
    }[];
  } | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${import.meta.env.VITE_API_URL}/stats`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }).then((res) => res.json() as Promise<StatsResponse>);

        if (response.ok) {
          setStats(response.data.stats);
        } else {
          setError(response.error || "Failed to fetch stats");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Error fetching stats data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <main className="flex h-full w-full flex-col items-center overflow-auto">
      <section className="flex h-full w-full flex-col px-4 pb-10 md:max-w-6xl">
        <div className="mt-4 flex w-full justify-between md:mt-10">
          <h1 className="py-2 text-2xl font-bold text-sky-800">MedSpot.fr 🩺</h1>
        </div>
        <h2 className="my-5 text-2xl text-orange-400 md:max-w-[70%]">
          Stats ({stats?.totalUsers} utilisateurs aujourd'hui)
        </h2>

        {loading && <div className="text-center py-10">Loading stats...</div>}
        {error && <div className="text-center py-10 text-red-500">{error}</div>}

        {!!stats && (
          <div className="flex flex-col gap-6 mb-10">
            {/* Daily Active Users Chart with Post Count Badges */}
            <div className="h-80 sm:h-96 bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg sm:text-xl font-semibold">DAU - Daily Active Users</h2>
              <p className="text-xs mb-2 sm:mb-4">
                Le nombre d'utilisateurs uniques qui sont venus au moins une fois dans la journée
                <span className="ml-1 text-sky-500 font-medium">(badges bleus = nombre de posts du jour)</span>
              </p>
              <div className="overflow-x-auto h-full">
                <div className="h-[calc(100%-60px)] min-w-[1200px]">
                  <ResponsiveBar
                    data={stats.dau}
                    keys={["count"]}
                    indexBy="date"
                    margin={{ top: 10, right: 30, bottom: 50, left: 40 }}
                    padding={0.3}
                    colors={{ scheme: "nivo" }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: "Date",
                      legendPosition: "middle",
                      legendOffset: 40,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: "Users",
                      legendPosition: "middle",
                      legendOffset: -40,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    animate={true}
                    label={(d) => `${d.value} (${d.data.percentage || 0}%)`}
                    layers={[
                      "grid",
                      "axes",
                      "bars",
                      "markers",
                      "legends",
                      "annotations",
                      // Custom layer for post count badges
                      (props) => {
                        const { bars } = props;
                        return (
                          <g>
                            {bars.map((bar) => {
                              const data = bar.data.data;
                              const postCount = data.postCount || 0;
                              if (postCount === 0) return null;

                              // Position at the bottom inside the bar
                              // Leave a small margin from the bottom (5px)
                              const yPosition = bar.y + bar.height - 15;

                              // Only show badge if bar is tall enough
                              if (bar.height < 30) return null;

                              return (
                                <g
                                  key={`${bar.key}-badge`}
                                  transform={`translate(${bar.x + bar.width / 2}, ${yPosition})`}
                                >
                                  <circle r="12" fill="#0ea5e9" />
                                  <text
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill="white"
                                    style={{
                                      fontSize: postCount > 99 ? "8px" : "10px",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {postCount}
                                  </text>
                                </g>
                              );
                            })}
                          </g>
                        );
                      },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Weekly Active Users Chart */}
            <div className="h-80 sm:h-96 bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg sm:text-xl font-semibold">WAU - Weekly Active Users</h2>
              <p className="text-xs mb-2 sm:mb-4">
                Le nombre d'utilisateurs uniques qui sont venus au moins une fois dans la semaine
              </p>
              <div className="overflow-x-auto h-full">
                <div className="h-[calc(100%-60px)]">
                  <ResponsiveBar
                    data={stats.wau}
                    keys={["count"]}
                    indexBy="date"
                    margin={{ top: 10, right: 20, bottom: 50, left: 40 }}
                    padding={0.3}
                    colors={{ scheme: "category10" }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: "Week",
                      legendPosition: "middle",
                      legendOffset: 32,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: "Users",
                      legendPosition: "middle",
                      legendOffset: -40,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    animate={true}
                    label={(d) => `${d.value} (${d.data.percentage || 0}%)`}
                  />
                </div>
              </div>
            </div>

            {/* Monthly Active Users Chart */}
            <div className="h-80 sm:h-96 bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg sm:text-xl font-semibold">MAU - Monthly Active Users</h2>
              <p className="text-xs mb-2 sm:mb-4">
                Le nombre d'utilisateurs uniques qui sont venus au moins une fois dans le mois
              </p>
              <div className="overflow-x-auto h-full">
                <div className="h-[calc(100%-60px)]">
                  <ResponsiveBar
                    data={stats.mau}
                    keys={["count"]}
                    indexBy="month"
                    margin={{ top: 10, right: 20, bottom: 50, left: 40 }}
                    padding={0.3}
                    colors={{ scheme: "paired" }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: "Month",
                      legendPosition: "middle",
                      legendOffset: 32,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: "Users",
                      legendPosition: "middle",
                      legendOffset: -40,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    animate={true}
                    label={(d) => `${d.value} (${d.data.percentage || 0}%)`}
                  />
                </div>
              </div>
            </div>

            {/* Monthly Engagement Chart */}
            <div className="h-[40rem] bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg sm:text-xl font-semibold">Monthly User Engagement</h2>
              <p className="text-xs mb-2 sm:mb-4">Répartition des utilisateurs par niveau d'engagement</p>
              <div className="overflow-x-auto h-[calc(100%-4rem)]">
                <div className="h-full">
                  {stats.monthlyEngagement.length > 0 ? (
                    <ResponsiveBar
                      data={stats.monthlyEngagement}
                      keys={["Créateurs de contenu", "Engagés (post, like)", "Visiteurs", "Inactifs"]}
                      indexBy="month"
                      margin={{ top: 20, right: 130, bottom: 150, left: 60 }}
                      padding={0.3}
                      groupMode="grouped"
                      valueScale={{ type: "linear" }}
                      indexScale={{ type: "band", round: true }}
                      colors={{ scheme: "set2" }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -10,
                        // legend: "Month",
                        legendPosition: "middle",
                        legendOffset: 40,
                        truncateTickAt: 0,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: "Users",
                        legendPosition: "middle",
                        legendOffset: -40,
                        truncateTickAt: 0,
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      label={(d) => {
                        const dataKey = d.id as string;
                        const percentageKey = `${dataKey} (%)` as keyof typeof d.data;
                        const percentage = d.data[percentageKey] || 0;
                        return `${d.value} (${percentage}%)`;
                      }}
                      legends={[
                        {
                          dataFrom: "keys",
                          anchor: "bottom-right",
                          direction: "column",
                          justify: false,
                          translateX: -50,
                          translateY: 120,
                          itemsSpacing: 2,
                          itemWidth: 100,
                          itemHeight: 20,
                          itemDirection: "left-to-right",
                          itemOpacity: 0.85,
                          symbolSize: 20,
                          effects: [
                            {
                              on: "hover",
                              style: {
                                itemOpacity: 1,
                              },
                            },
                          ],
                        },
                      ]}
                      role="application"
                      ariaLabel="Monthly user engagement"
                      animate={true}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-gray-500">
                      No engagement data available
                    </div>
                  )}
                </div>
              </div>
            </div>
            {/* Cumulative Users Chart */}
            <div className="h-80 sm:h-96 bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg sm:text-xl font-semibold">Nombre d'utilisateurs cumulé</h2>
              <p className="text-xs mb-2 sm:mb-4">Le nombre total d'utilisateurs inscrits au fil du temps</p>
              <div className="overflow-x-auto h-full">
                <div className="h-[calc(100%-60px)]">
                  <ResponsiveLine
                    data={[
                      {
                        id: "Nombre d'utilisateurs",
                        data: stats.cumulativeUsers.map((item) => ({
                          x: item.date,
                          y: item.count,
                        })),
                      },
                    ]}
                    margin={{ top: 10, right: 20, bottom: 50, left: 40 }}
                    xScale={{ type: "point" }}
                    yScale={{
                      type: "linear",
                      min: 0,
                      max: "auto",
                    }}
                    curve="monotoneX"
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: "Date",
                      legendPosition: "middle",
                      legendOffset: 40,
                      tickValues: stats.cumulativeUsers.filter((_, i) => i % 7 === 0).map((d) => d.date),
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: "Total Users",
                      legendPosition: "middle",
                      legendOffset: -40,
                    }}
                    colors={{ scheme: "category10" }}
                    pointSize={0}
                    // pointSize={10}
                    // pointColor={{ theme: "background" }}
                    // pointBorderWidth={2}
                    // pointBorderColor={{ from: "serieColor" }}
                    // pointLabelYOffset={-12}
                    enableArea={true}
                    areaOpacity={0.15}
                    useMesh={true}
                    enableSlices="x"
                    sliceTooltip={({ slice }) => {
                      const point = slice.points[0];
                      const data = stats.cumulativeUsers.find((d) => d.date === point.data.x);
                      return (
                        <div className="bg-white p-2 shadow rounded border border-gray-200">
                          <div className="text-sm font-semibold">{String(point.data.x)}</div>
                          <div className="text-sm">
                            Users: <strong>{String(point.data.y)}</strong>
                            {data?.percentage !== undefined && <span> ({data.percentage}%)</span>}
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <h2 className="my-5 text-2xl text-orange-400 md:max-w-[70%]">Stats de MedSpot globalement</h2>
            {/* Total Users Display */}
            <div className="text-center py-6">
              <span className="text-xl sm:text-2xl font-bold text-sky-800">
                Nombre total d'utilisateurs: {stats.onboardingFunnel.totalUsersForOnboarding}
              </span>
            </div>
            {/* Onboarding Funnel - Using Funnel Chart */}
            <div className="h-80 sm:h-96 bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg sm:text-xl font-semibold">Onboarding Funnel</h2>
              <p className="text-xs mb-2 sm:mb-4">Progression des utilisateurs à travers le processus d'onboarding</p>
              <div className="h-[calc(100%-60px)]">
                <ResponsiveFunnel
                  data={[
                    {
                      id: "Total inscrits avec email",
                      value: stats.onboardingFunnel.usersWithEmail,
                      label: "Total inscrits avec email",
                    },
                    {
                      id: "Enregistrement terminé",
                      value: stats.onboardingFunnel.usersWithFirstName,
                      label: "Enregistrement terminé",
                    },
                    {
                      id: "Avec Notifications",
                      value: stats.onboardingFunnel.usersWithNativePush,
                      label: "Avec Notifications",
                    },
                  ]}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  valueFormat={(value) =>
                    `${value} (${Math.round((value / stats.onboardingFunnel.totalUsersForOnboarding) * 100)}%)`
                  }
                  colors={{ scheme: "spectral" }}
                  borderWidth={20}
                  labelColor={{
                    from: "color",
                    modifiers: [["darker", 3]],
                  }}
                  direction="horizontal"
                  shapeBlending={0.5}
                  beforeSeparatorLength={20}
                  beforeSeparatorOffset={10}
                  afterSeparatorLength={20}
                  afterSeparatorOffset={10}
                  motionConfig="gentle"
                  enableLabel={true}
                />
              </div>
            </div>

            {/* Room Activity Chart */}
            <div className="h-80 sm:h-96 bg-white p-4 sm:p-6 rounded-lg shadow">
              <h2 className="text-lg sm:text-xl font-semibold">Room Activity</h2>
              <p className="text-xs mb-2 sm:mb-4">Posts par jour répartis par room</p>
              <div className="h-[calc(100%-60px)]">
                {Object.keys(stats.roomActivity).length > 0 ? (
                  <ResponsiveBar
                    data={Object.entries(stats.roomActivity).map(([date, rooms]) => {
                      // Convert room data to a flat object for the chart
                      const dataForDate: Record<string, string | number> = { date };
                      for (const room of rooms) {
                        dataForDate[room.roomName] = room.postCount;
                      }
                      return dataForDate;
                    })}
                    keys={Array.from(
                      new Set(Object.values(stats.roomActivity).flatMap((rooms) => rooms.map((r) => r.roomName)))
                    )}
                    indexBy="date"
                    margin={{ top: 20, right: 130, bottom: 50, left: 60 }}
                    padding={0.3}
                    groupMode="grouped"
                    colors={{ scheme: "nivo" }}
                    axisBottom={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: -45,
                      legend: "Date",
                      legendPosition: "middle",
                      legendOffset: 40,
                    }}
                    axisLeft={{
                      tickSize: 5,
                      tickPadding: 5,
                      tickRotation: 0,
                      legend: "Posts",
                      legendPosition: "middle",
                      legendOffset: -40,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    legends={[
                      {
                        dataFrom: "keys",
                        anchor: "bottom-right",
                        direction: "column",
                        justify: false,
                        translateX: 120,
                        translateY: 0,
                        itemsSpacing: 2,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: "left-to-right",
                        itemOpacity: 0.85,
                        symbolSize: 20,
                      },
                    ]}
                    animate={true}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    No room activity data available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
